import { SDKProvider } from '@littlebird/sdk';
import { ErrorBoundary } from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { Agentation } from 'agentation';
import { type KBarOptions, KBarProvider } from 'kbar';
import { posthog } from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import React from 'react';
import ReactDOM from 'react-dom/client';

import './styles/index.css';

import { getEnvVar } from '@/env';
import { setupAuthListeners } from '@/lib/auth';
import { inElectron } from '@/utils/capabilities';
import { initializeSentry } from '@/utils/errorReporting';
import { ProductFruitsWrapper } from '@/utils/ProductFruitsWrapper';

import { ExternalLinkHandler } from './components/ExternalLinkHandler';
import { HydrateMobxStores } from './components/HydrateMobxStores';
import {
  DisplayModals,
  ModalsManager,
} from './components/modals/modalsManager';
import { BottomSheet } from './components/ui/BottomSheet';
import { Toaster } from './components/ui/toaster';
import { TooltipProvider } from './components/ui/Tooltip';
import { UpdateActivityReporter } from './components/UpdateActivityReporter';
import { initializeCodingAgentRuns } from './features/action-cards/coding-agent-runs';
import { relayChatSendsAcrossWindows } from './features/chat/chat.events';
import { ContextCollectionProvider } from './features/context-collection/context-collection-provider';
import { MeetingQuitGuard } from './features/meetings/MeetingQuitGuard';
import {
  PaywallNotification,
  SubscriptionModalsManager,
} from './features/subscription';
import { DeviceListsProvider } from './features/transcription-input-device';
import { clearHash } from './hooks/useHashRoute';
import { initializeBootstrapListeners } from './lib/bootstrap-listeners';
import { pluginsStore, type PluginModule } from './plugins/plugins.store';
import { RouterProvider } from './providers/RouterProvider';
import { ThemeProvider } from './providers/ThemeProvider';
import { queryClient } from './query/client';
import { ErrorScreen } from './screens/app/ErrorScreen';
import { uiStore } from './stores/ui.store';
import { initializePosthog } from './utils/analytics';
import { initSingular } from './utils/singular';

const isElectron = inElectron();

if (isElectron) {
  document.documentElement.setAttribute('data-platform', 'electron');
}

const shouldShowAgentation =
  import.meta.env.DEV && isElectron && getEnvVar('VITE_ENABLE_AGENTATION');

initializeSentry();
initializePosthog();
initSingular();

initializeBootstrapListeners();
initializeCodingAgentRuns();
relayChatSendsAcrossWindows();
// The one place core reaches plugin code. Vite emits a chunk per plugin and fetches it only when its flag is on
pluginsStore.start(
  import.meta.glob<PluginModule>('../../../plugins/*/desktop/plugin.ts')
);
setupAuthListeners();

const commandBarOptions: KBarOptions = {
  animations: {
    enterMs: 150,
    exitMs: 150,
  },
  disableScrollbarManagement: true,
  callbacks: {
    onOpen: () => {
      clearHash();
      ModalsManager.closeAll();
    },
    onQueryChange: (query) => uiStore.setCommandBarQuery(query),
  },
};

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorScreen error={error as Error} reset={resetError} />
      )}
    >
      <SDKProvider>
        <UpdateActivityReporter />
        <HydrateMobxStores>
          <QueryClientProvider client={queryClient}>
            <PostHogProvider client={posthog}>
              <ThemeProvider>
                <TooltipProvider>
                  <DeviceListsProvider>
                    <BottomSheet.Provider>
                      <ContextCollectionProvider>
                        <KBarProvider actions={[]} options={commandBarOptions}>
                          <ExternalLinkHandler>
                            <RouterProvider />
                            <DisplayModals />
                            <PaywallNotification />
                            <SubscriptionModalsManager />
                            <MeetingQuitGuard />
                            <Toaster />
                            <ProductFruitsWrapper />
                          </ExternalLinkHandler>
                        </KBarProvider>
                      </ContextCollectionProvider>
                    </BottomSheet.Provider>
                  </DeviceListsProvider>
                </TooltipProvider>
              </ThemeProvider>
            </PostHogProvider>
          </QueryClientProvider>
        </HydrateMobxStores>
        {shouldShowAgentation ? <Agentation /> : null}
      </SDKProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
